import type { AnswerOutcome, ChallengeAnswer } from '../api/models';

import type { SessionState, TaskState } from './sessionState';
import { acceptsInput, canSubmit, currentTask, isClosed, taskStateFor } from './sessionState';

/**
 * Ported from frontend/shared/src/commonMain/kotlin/sk/myslikrok/shared/Session/SessionMachine.kt.
 * A change to the Kotlin changes this file in the same commit — client code manifest §2a.
 *
 * The hint ladder, as a pure function of state and server outcome. Three attempts, a hint on the first
 * two, an explanation and a replacement task on the third, and an expiry that arrives as data rather than
 * a timer.
 *
 * A divergence here does not look like a bug — it looks like the child answering differently, which the
 * API then records as that child's error data and feeds into level adaptation. That is why this is the
 * one file in the web client with its own tests.
 *
 * It decides nothing about correctness and composes no text. The server did both.
 */
export const SessionMachine = {
  /** The child changed their choice. */
  select: (state: TaskState, optionKey: string): TaskState =>
    !acceptsInput(state)
      ? state
      : {
          ...state,
          selectedOptionKeys: [optionKey],
          // Choosing again after a hint clears the hint but keeps the attempt count.
          feedbackText: state.phase === 'retrying' ? null : state.feedbackText,
        },

  type: (state: TaskState, text: string): TaskState =>
    !acceptsInput(state) ? state : { ...state, typedText: text },

  reorder: (state: TaskState, ordering: string[]): TaskState =>
    !acceptsInput(state) ? state : { ...state, ordering },

  submitting: (state: TaskState): TaskState =>
    !canSubmit(state) ? state : { ...state, phase: 'submitting' },

  /**
   * Folds the server's outcome into the task.
   *
   * Correct → `solved`. Wrong with attempts left → `retrying`, the selection cleared so the child's choice
   * is not left marked. Wrong on the last attempt, or expired → `closed`.
   */
  applyOutcomeToTask: (state: TaskState, outcome: AnswerOutcome): TaskState => {
    if (outcome.isCorrect) {
      return {
        ...state,
        phase: 'solved',
        feedbackText: outcome.message,
        feedbackTone: 'affirm',
        attemptsRemaining: 0,
      };
    }

    if (outcome.status === 'open') {
      return {
        ...state,
        phase: 'retrying',
        // Cleared, not marked. The design system forbids marking a wrong choice.
        selectedOptionKeys: [],
        ordering: [],
        feedbackText: outcome.message,
        feedbackTone: 'hint',
        attemptsRemaining: outcome.attemptsRemaining,
      };
    }

    return {
      ...state,
      phase: 'closed',
      feedbackText: outcome.explanation ?? outcome.message,
      feedbackTone: 'hint',
      attemptsRemaining: 0,
    };
  },

  /** Folds the outcome into the whole session: the task, any replacement, and completion. */
  applyOutcome: (session: SessionState, outcome: AnswerOutcome): SessionState => {
    const task = currentTask(session);

    if (task === null) return session;

    const tasks = [...session.tasks];

    tasks[session.currentIndex] = SessionMachine.applyOutcomeToTask(task, outcome);

    // A replacement is appended, never substituted: the failed task stays in the rail because it happened,
    // and the new one is genuinely additional (BR-10).
    if (outcome.replacementItem) tasks.push(taskStateFor(outcome.replacementItem));

    return {
      ...session,
      tasks,
      isComplete: outcome.sessionCompleted,
      completionMessage: outcome.sessionMessage ?? session.completionMessage,
      rewardMessage: outcome.rewardMessage ?? session.rewardMessage,
      starsEarned: outcome.sessionCompleted ? outcome.starsEarned : session.starsEarned,
      coinsEarned: outcome.sessionCompleted ? outcome.coinsEarned : session.coinsEarned,
      // Accumulated rather than assigned. Achievements arrive on the answer that crossed the threshold,
      // and more than one answer in a session can cross one.
      earnedAchievements: [...session.earnedAchievements, ...(outcome.earnedAchievements ?? [])],
    };
  },

  /**
   * Builds the payload for the current answer.
   *
   * Which field carries the answer depends on the presentation, and getting it wrong means the API reads
   * an empty answer and marks a child wrong for a bug.
   */
  answerFor: (state: TaskState, clientReportedTimeMs: number | null = null): ChallengeAnswer => {
    const instanceToken = state.item.instanceToken;

    switch (state.item.presentation) {
      case 'multiple_choice':
      case 'grid_selection':
        return { instanceToken, optionKeys: state.selectedOptionKeys, clientReportedTimeMs };
      case 'text_answer':
        return { instanceToken, text: state.typedText.trim(), clientReportedTimeMs };
      case 'ordering':
      case 'sequence_recall':
        return { instanceToken, ordering: state.ordering, clientReportedTimeMs };
      default:
        // An unknown presentation reaches here only if the API gained one the client has not been taught.
        // Send whatever the child managed to give rather than nothing, and let the server judge it.
        return {
          instanceToken,
          optionKeys: state.selectedOptionKeys.length > 0 ? state.selectedOptionKeys : null,
          text: state.typedText.trim().length > 0 ? state.typedText.trim() : null,
          ordering: state.ordering.length > 0 ? state.ordering : null,
          clientReportedTimeMs,
        };
    }
  },

  /**
   * Moves to the next task that is still open — **forwards first**.
   *
   * It used to take the first open task in the list, which sent a child backwards: a task failed early
   * leaves its replacement appended at the end, and on reaching „Úloha 9 z 9" the rail jumped to
   * „Úloha 4 z 9" with no explanation (R0103).
   *
   * Wrapping to an earlier open task is still right once there is nothing ahead — that task is work the
   * child genuinely still owes — but it happens at the end of the session rather than in the middle of it.
   */
  advance: (session: SessionState): SessionState => {
    const ahead = session.tasks.findIndex(
      (task: TaskState, index: number): boolean => index > session.currentIndex && !isClosed(task),
    );

    if (ahead >= 0) return { ...session, currentIndex: ahead };

    const behind = session.tasks.findIndex((task: TaskState): boolean => !isClosed(task));

    return behind >= 0 ? { ...session, currentIndex: behind } : session;
  },
};
